import { useSnackbar } from 'notistack';
import { useCallback } from 'react';

import {
  CustomDataFieldAttributes,
  CustomFieldValues,
} from '~/data/customData';
import { DocumentType, useGetCompanyDocuments } from '~/data/documents';
import { FullEmploymentData } from '~/data/employment/types';
import { Enum } from '~/data/enums';
import { EmployeeHistoryType } from '~/data/history/useEmployeeHistory';
import { EmployeeOtherHistoryType } from '~/data/history/useEmployeeOtherHistory';
import { PendingScheduleChanges } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/useScheduleChanges';
import {
  DocumentsEntry,
  convertDocumentTypeToDocumentsEntry,
} from '~/scenes/PeopleModule/components/Tabs/DocumentsTab/DocumentsTable/data';
import { getENVs } from '~/utils/helper';

import {
  generateDocument,
  getDocumentWorkContractPlaceholders,
  jsPersonName,
} from './data/printDocx';
import { useGetPlaceholderDataDocx } from './useGetPlaceholderDataDocx';

type UsePrintWorkContractsOutput = {
  isLoading: boolean;
  workContractDocuments: DocumentsEntry[];
  mutate: () => void;
  onPrintWorkContract: (
    documentId: number,
    selectedEmployment?: PendingScheduleChanges,
  ) => Promise<void>;
};

export const usePrintWorkContracts = (
  employmentId?: number,
): UsePrintWorkContractsOutput => {
  const { enqueueSnackbar } = useSnackbar();

  const {
    empId,
    isCustomDataEnabled,
    jsonEnumALL,
    jsonAllEmp,
    jsonOrgUnitData,
    jsonPrimaryUnitData,
    jsonOrgUnitDataInclDeleted,
    jsonOffices,
    t,
    jsonLegalEntities,
    jsonCostCenters,
    jsonContractEmployeeData,
    jsonContractEmploymentData,
    jsonContractDevHistoricalData,
    jsonContractDevCurrentData,
    jsonContractCustomDataPeople,
    jsonCustomContractEmployeeFieldAttr,
    jsonCustomContractEmployeeFieldsTranslation,
    jsonContractCustomDataPosition,
    jsonCustomContractPositionFieldAttr,
    jsonCustomContractPositionFieldsTranslation,
    jsonShortAbsences,
    jsonLongAbsences,
    jsonDevOtherExperienceData,
    isLoading,
  } = useGetPlaceholderDataDocx(false, employmentId);

  const {
    data: docList,
    isLoading: isLoadingDocList,
    mutate: mutateDocList,
  } = useGetCompanyDocuments();

  const fillDocumentWorkContract = useCallback(
    (
      document: DocumentType,
      empId: number,
      selectedEmployment?: PendingScheduleChanges,
    ) => {
      return getDocumentWorkContractPlaceholders(
        selectedEmployment as PendingScheduleChanges,
        isCustomDataEnabled,
        jsonEnumALL,
        jsonAllEmp,
        jsonOrgUnitData,
        jsonPrimaryUnitData,
        jsonOrgUnitDataInclDeleted,
        jsonOffices,
        t,
        jsonLegalEntities,
        jsonCostCenters,
        jsonContractEmployeeData,
        jsonContractEmploymentData as FullEmploymentData,
        jsonContractDevHistoricalData as EmployeeHistoryType[],
        jsonContractDevCurrentData as EmployeeHistoryType[],
        jsonContractCustomDataPeople as CustomFieldValues,
        jsonCustomContractEmployeeFieldAttr as CustomDataFieldAttributes[],
        jsonCustomContractEmployeeFieldsTranslation as Enum[][],
        jsonContractCustomDataPosition as CustomFieldValues,
        jsonCustomContractPositionFieldAttr as CustomDataFieldAttributes[],
        jsonCustomContractPositionFieldsTranslation as Enum[][],
        jsonShortAbsences ?? [],
        jsonLongAbsences ?? [],
        jsonDevOtherExperienceData as EmployeeOtherHistoryType[],
      )
        .then((placeholders) => {
          const oldApiHostname = getENVs('REACT_APP_OLD_API_HOSTNAME');
          const url =
            oldApiHostname +
            '/d/bin/company/file/' +
            document.fDocumentId +
            '?inline=true&v=' +
            Math.random().toString(36).slice(2);
          const outputFileName = `${
            document.fDocumentDescription
          } (${jsPersonName(empId, jsonAllEmp, false, ' ')})`;
          return generateDocument<object>(
            url,
            placeholders as object,
            outputFileName,
          );
        })
        .catch((err) => {
          console.log('fillDocumentWorkContract', err);
        });
    },
    [
      isCustomDataEnabled,
      jsonEnumALL,
      jsonAllEmp,
      jsonOrgUnitData,
      jsonPrimaryUnitData,
      jsonOrgUnitDataInclDeleted,
      jsonOffices,
      t,
      jsonLegalEntities,
      jsonCostCenters,
      jsonContractEmployeeData,
      jsonContractEmploymentData,
      jsonContractDevHistoricalData,
      jsonContractDevCurrentData,
      jsonContractCustomDataPeople,
      jsonCustomContractEmployeeFieldAttr,
      jsonCustomContractEmployeeFieldsTranslation,
      jsonContractCustomDataPosition,
      jsonCustomContractPositionFieldAttr,
      jsonCustomContractPositionFieldsTranslation,
      jsonShortAbsences,
      jsonLongAbsences,
      jsonDevOtherExperienceData,
    ],
  );

  const onPrintWorkContract = useCallback(
    (
      documentId: number,
      selectedEmployment?: PendingScheduleChanges,
    ): Promise<void> => {
      const document = docList.find((doc) => doc.fDocumentId === documentId);

      if (document?.fDocumentName?.endsWith('.docx') && !isLoading) {
        fillDocumentWorkContract(
          document as DocumentType,
          empId,
          selectedEmployment,
        ).catch(function (err) {
          enqueueSnackbar('Error printing document', { variant: 'error' });
          console.log('onPrintWorkContract', err);
        });
      }

      if (document?.fDocumentName?.endsWith('.html')) {
        const strRdm = Math.random().toString(36);
        const origin = window.location.origin;
        window.open(
          `${origin}/d/bin/company/file/${documentId}?inline=true&v=${strRdm}`,
          '_blank',
        );
      }

      return Promise.reject();
    },
    [enqueueSnackbar, docList, empId, fillDocumentWorkContract, isLoading],
  );

  return {
    isLoading: isLoadingDocList,
    workContractDocuments: docList.map(convertDocumentTypeToDocumentsEntry),
    mutate: () => {
      mutateDocList();
    },
    onPrintWorkContract,
  };
};
